'use server'

import { apiRequest, ApiResponse } from '../request'

/**
 * Categoria do modelo.
 *
 * Espelha o enum `TemplateCategory` do backend. Cada categoria tem FORMULÁRIO
 * próprio — é por isso que é enum e não uma tabela editável: uma categoria que a
 * tela não saiba renderizar nasce quebrada.
 */
export enum TemplateCategory {
    /** Pedido de exames: o grupo que o médico sempre solicita junto. */
    EXAM_REQUEST = 'EXAM_REQUEST',
    /** Laudo / relatório médico: texto corrido, com formatação. */
    REPORT = 'REPORT',
    /** Atestado. */
    CERTIFICATE = 'CERTIFICATE',
    /** Receituário: o texto da receita mais os medicamentos que a acompanham. */
    PRESCRIPTION = 'PRESCRIPTION',
}

export const TEMPLATE_CATEGORY_LABELS: Record<TemplateCategory, string> = {
    [TemplateCategory.EXAM_REQUEST]: 'Pedido de exames',
    [TemplateCategory.REPORT]: 'Laudo',
    [TemplateCategory.CERTIFICATE]: 'Atestado',
    [TemplateCategory.PRESCRIPTION]: 'Receituário',
}

/** O que cada categoria significa, na hora de escolher. */
export const TEMPLATE_CATEGORY_HINTS: Record<TemplateCategory, string> = {
    [TemplateCategory.EXAM_REQUEST]:
        'Um grupo de exames que você sempre pede junto — check-up, pré-operatório, acompanhamento.',
    [TemplateCategory.REPORT]:
        'O texto padrão de um laudo ou relatório médico, com as variáveis do paciente.',
    [TemplateCategory.CERTIFICATE]:
        'O texto padrão de um atestado, com as variáveis do paciente e do período.',
    [TemplateCategory.PRESCRIPTION]:
        'A receita que você repete — o texto e a lista de medicamentos com dose, frequência e duração.',
}

export interface TemplateItem {
    id: number
    catalogId: number
    /** Variações-como-item escolhidas. Vazio = item sem variação. */
    variationCatalogIds: number[]
    sortOrder: number
    catalog?: {
        id: number
        name: string
        category: string
        collectionMaterial?: string | null
    }
}

/** Um medicamento do modelo de receita. Espelha `PrescriptionItem`. */
export interface TemplateMedication {
    medication: string
    dosage?: string
    frequency?: string
    duration?: string
    instructions?: string
    quantity?: number
}

export interface Template {
    id: number
    name: string
    category: TemplateCategory
    /**
     * Corpo do documento com variáveis `{{...}}`. HTML em REPORT e CERTIFICATE;
     * texto puro em PRESCRIPTION (a tela da receita é uma textarea).
     */
    content?: string | null
    /** Indicação clínica do pedido (EXAM_REQUEST) ou observações da receita (PRESCRIPTION). */
    notes?: string | null
    /** Medicamentos do modelo. Só PRESCRIPTION. */
    medications?: TemplateMedication[] | null
    icdCodeId?: number | null
    icdCode?: { id: number; code: string; description: string } | null
    professionalId: number
    professional?: {
        id: number
        name: string
        councilName?: string | null
        registrationNumber?: string | null
    }
    organizationId: number
    items: TemplateItem[]
    createdAt: string
    updatedAt: string
}

/** Uma variável oferecida na barra do editor. */
export interface TemplateVariable {
    key: string
    label: string
    hint: string
}

export interface CreateTemplateItemData {
    catalogId: number
    variationCatalogIds?: number[]
    sortOrder?: number
}

export interface CreateTemplateData {
    name: string
    category: TemplateCategory
    content?: string
    notes?: string
    icdCodeId?: number
    professionalId: number
    items?: CreateTemplateItemData[]
    medications?: TemplateMedication[]
}

/**
 * O modelo com as variáveis já trocadas pelos dados do paciente.
 *
 * Quem resolve `{{paciente}}` é o BACKEND: o mesmo texto vai para a tela e para o
 * PDF, e duas implementações divergiriam na primeira variável nova.
 */
export interface AppliedTemplate {
    templateId: number
    name: string
    category: TemplateCategory
    content: string | null
    notes: string | null
    icdCodeId: number | null
    items: TemplateItem[]
    medications: TemplateMedication[]
}

export type UpdateTemplateData = Partial<CreateTemplateData>

export async function getTemplates(params?: {
    professionalId?: number
    category?: TemplateCategory
}): Promise<ApiResponse<Template[]>> {
    return apiRequest<Template[]>('templates', { params: params ?? {} })
}

export async function getTemplate(id: number): Promise<ApiResponse<Template>> {
    return apiRequest<Template>(`templates/${id}`, {})
}

/**
 * As variáveis vêm do BACKEND, não de uma constante aqui: é ele que as resolve na
 * emissão, e duas listas divergiriam sem ninguém perceber até o laudo sair com um
 * espaço em branco no lugar do nome do paciente.
 */
export async function getTemplateVariables(): Promise<
    ApiResponse<TemplateVariable[]>
> {
    return apiRequest<TemplateVariable[]>('templates/variables', {})
}

export async function createTemplate(
    data: CreateTemplateData
): Promise<ApiResponse<Template>> {
    return apiRequest<Template>('templates', { data }, 'post')
}

export async function updateTemplate(
    id: number,
    data: UpdateTemplateData
): Promise<ApiResponse<Template>> {
    return apiRequest<Template>(`templates/${id}`, { data }, 'patch')
}

export async function applyTemplate(
    id: number,
    data: {
        clientId: number
        professionalId?: number
        icdCodeId?: number
    }
): Promise<ApiResponse<AppliedTemplate>> {
    return apiRequest<AppliedTemplate>(
        `templates/${id}/apply`,
        { data },
        'post'
    )
}

export async function deleteTemplate(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`templates/${id}`, {}, 'delete')
}
